import { skipToken } from '@reduxjs/toolkit/query';
import Loader from 'components/Loader';
import { useIsNewDesign } from 'components/tables/list-table/filter-bar/useIsNewDesign';
import { useMemo } from 'react';
import { Navigate, useLocation, useParams } from 'react-router';
import { useGetJobRoleByIdQuery } from 'store/api/jobRoles';
import { JobRoleForm } from 'types/jobRoles';
import { useClassifiers } from 'utils/useClassifiers';
import { JOB_ROLE_FORM_CLASSIFIERS } from './form-sections/useJobRoleForm';
import { jobRoleDtoToForm } from './jobRoleFormMapping';
import JobRolePageForm from './JobRolePageForm';

const ClassicJobRolePageEdit = () => {
    const { id: jobRoleId } = useParams();

    const { classifiers, isLoading: isLoadingClassifiers } = useClassifiers({
        codes: JOB_ROLE_FORM_CLASSIFIERS,
    });

    const { data: jobRole, isLoading, isError, refetch } = useGetJobRoleByIdQuery(jobRoleId ?? skipToken);

    // Derive initial form data from classifiers + fetched job role during render (no effect needed).
    const initialData = useMemo<JobRoleForm | undefined>(() => {
        if (!classifiers) return undefined;
        return jobRoleDtoToForm(jobRole, classifiers);
    }, [classifiers, jobRole]);

    if (isLoading || isLoadingClassifiers || (initialData === undefined && !isError)) {
        return <Loader />;
    }

    return (
        <JobRolePageForm
            type="edit"
            initialData={initialData}
            classifiers={classifiers}
            refetch={() => {
                void refetch();
            }}
            readOnlyExistingJobRole={jobRole}
            entityCustomFields={jobRole?.customFields}
        />
    );
};

/**
 * In the new design a job role is edited card by card on its page, so its edit URL opens the first card there, on
 * the path it came through (the Admin area's or the plain one).
 */
const JobRolePageEdit = () => {
    const isNew = useIsNewDesign();
    const { pathname } = useLocation();
    return isNew ? (
        <Navigate to={`${pathname.replace(/\/edit\/?$/, '')}?edit=job-role`} replace />
    ) : (
        <ClassicJobRolePageEdit />
    );
};

export default JobRolePageEdit;
